/**
 * LinkAccount.js
 *
 * Route: /link?token=...  (from SynergenHR link-init)
 *   OR   /link?synergen_email=...&synergen_user_id=...  (from SsoLanding not-linked state)
 *
 * Flow A — token in URL (SynergenHR initiated):
 *  1. User is already logged into Strategizer (JWT in localStorage)
 *     OR needs to log in first
 *  2. Show "Connect your accounts" confirmation
 *  3. On confirm → POST /sso/link with the link token
 *  4. Success → show confirmation, close tab or redirect
 *
 * Flow B — synergen_email in URL (from SsoLanding):
 *  1. User needs to log into Strategizer first
 *  2. After login, the link token is fetched from SynergenHR
 *  3. Same confirmation + link flow
 */
import React, { useEffect, useState } from "react";
import { SsoLinkApi } from "../../api/auth/SsoApi";
import LoginApi from "../../api/auth/LoginApi";

function LinkAccount() {
  const params = new URLSearchParams(window.location.search);
  const linkToken = params.get("token");
  const synergenEmail = params.get("synergen_email") || "";

  const [phase, setPhase] = useState(
    linkToken ? "confirm" : "login"
  ); // login | confirm | linking | success | error
  const [message, setMessage] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [storedToken, setStoredToken] = useState(linkToken || "");

  // If already logged in and we have a link token, go straight to confirm
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    if (authToken && linkToken) {
      setPhase("confirm");
    }
  }, []);

  async function handleLogin(e) {
    e.preventDefault();
    setLoginError("");
    const res = await LoginApi(username, password);
    if (res.success && res.data?.customerAccessToken) {
      localStorage.setItem("authToken", res.data.customerAccessToken);
      setPhase("confirm");
    } else {
      setLoginError(res.message || "Login failed. Please check your credentials.");
    }
  }

  async function handleConfirm() {
    if (!storedToken) {
      setMessage("No link token available. Please try again from SynergenHR.");
      setPhase("error");
      return;
    }
    setPhase("linking");
    const res = await SsoLinkApi(storedToken);
    if (res.success) {
      setPhase("success");
    } else {
      setMessage(res.message || "Linking failed. Please try again.");
      setPhase("error");
    }
  }

  const styles = {
    page: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f8fafc",
      fontFamily: "sans-serif",
    },
    card: {
      background: "#fff",
      borderRadius: 12,
      padding: "40px 48px",
      boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
      maxWidth: 440,
      width: "100%",
    },
    title: { fontSize: 22, fontWeight: 700, color: "#1a3a5c", marginBottom: 8 },
    sub: { fontSize: 14, color: "#666", marginBottom: 24, lineHeight: 1.6 },
    input: {
      width: "100%",
      padding: "10px 14px",
      borderRadius: 8,
      border: "1px solid #ddd",
      fontSize: 14,
      marginBottom: 12,
      boxSizing: "border-box",
    },
    btn: {
      width: "100%",
      padding: "12px",
      borderRadius: 8,
      background: "#1a3a5c",
      color: "#fff",
      fontWeight: 600,
      fontSize: 14,
      border: "none",
      cursor: "pointer",
      marginBottom: 8,
    },
    btnSecondary: {
      width: "100%",
      padding: "12px",
      borderRadius: 8,
      background: "#f1f5f9",
      color: "#555",
      fontWeight: 600,
      fontSize: 14,
      border: "none",
      cursor: "pointer",
    },
    error: { color: "#e74c3c", fontSize: 13, marginBottom: 12 },
    emoji: { fontSize: 32, marginBottom: 12, display: "block" },
    divider: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      margin: "16px 0",
      color: "#aaa",
      fontSize: 12,
    },
    line: { flex: 1, height: 1, background: "#eee" },
  };

  if (phase === "login") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <span style={styles.emoji}>🔗</span>
          <div style={styles.title}>Link your Strategizer account</div>
          <div style={styles.sub}>
            Log into Strategizer to connect it with your SynergenHR account
            {synergenEmail ? ` (${synergenEmail})` : ""}.
          </div>
          <form onSubmit={handleLogin}>
            <input
              style={styles.input}
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
            <input
              style={styles.input}
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {loginError && <div style={styles.error}>{loginError}</div>}
            <button type="submit" style={styles.btn}>Log in &amp; Continue</button>
          </form>
        </div>
      </div>
    );
  }

  if (phase === "confirm") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <span style={styles.emoji}>🔗</span>
          <div style={styles.title}>Connect your accounts</div>
          <div style={styles.sub}>
            This will link your Strategizer account to your SynergenHR account.
            After linking, you can switch between both apps with one click.
          </div>
          <div style={{ ...styles.divider }}>
            <div style={styles.line} />
            <span>SynergenHR ↔ Strategizer</span>
            <div style={styles.line} />
          </div>
          <button style={styles.btn} onClick={handleConfirm}>
            ✅ Connect Accounts
          </button>
          <button style={styles.btnSecondary} onClick={() => window.close()}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (phase === "linking") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <span style={styles.emoji}>⏳</span>
          <div style={styles.title}>Linking accounts…</div>
        </div>
      </div>
    );
  }

  if (phase === "success") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <span style={styles.emoji}>✅</span>
          <div style={styles.title}>Accounts linked!</div>
          <div style={styles.sub}>
            Your SynergenHR and Strategizer accounts are now connected.
            You can close this tab and use the switcher in either app.
          </div>
          <button style={styles.btn} onClick={() => window.close()}>
            Close Tab
          </button>
        </div>
      </div>
    );
  }

  // error
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <span style={styles.emoji}>❌</span>
        <div style={styles.title}>Linking failed</div>
        <div style={styles.sub}>{message}</div>
        <button style={styles.btn} onClick={() => window.history.back()}>
          Go Back
        </button>
      </div>
    </div>
  );
}

export default LinkAccount;
